/**
 * Sub-field input used inside repeatable_array rows. Mirrors the main input
 * dispatch but with compact sizing and a constrained type set.
 */
import { cn } from '@/lib/utils/cn'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type {
  SelectOption,
  SubFieldDefinition,
} from '@/features/form-builder/api/form-builder-api'
import type { SubInputProps } from '../input-props'

export function SubFieldInput({ subField, value, onChange, onBlur, disabled }: SubInputProps) {
  const tc = subField.type_config ?? {}

  switch (subField.field_type) {
    case 'text':
      return (
        <Input
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          dir="auto"
          className="h-8 text-sm"
        />
      )
    case 'textarea':
      return (
        <Textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          rows={2}
          className="text-sm resize-none"
          dir="auto"
        />
      )
    case 'number':
      return (
        <Input
          type="number"
          value={value === null || value === undefined ? '' : (value as string | number)}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          onBlur={onBlur}
          disabled={disabled}
          dir="ltr"
          className="h-8 text-sm"
        />
      )
    case 'date':
    case 'time':
    case 'datetime': {
      const inputType =
        subField.field_type === 'date' ? 'date'
        : subField.field_type === 'time' ? 'time'
        : 'datetime-local'
      return (
        <Input
          type={inputType}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          dir="ltr"
          className="h-8 text-sm"
        />
      )
    }
    case 'boolean':
      return <SubBooleanButtons subField={subField} value={value} onChange={onChange} disabled={disabled} />
    case 'single_select':
      return <SubSingleSelect subField={subField} value={value} onChange={onChange} disabled={disabled} />
    case 'multi_select':
      return <SubMultiSelect subField={subField} value={value} onChange={onChange} disabled={disabled} />
    default:
      return (
        <Input
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          className="h-8 text-sm"
        />
      )
  }
}

function readLabelLoose(v: unknown): string | undefined {
  if (v == null) return undefined
  if (typeof v === 'string') return v
  if (typeof v === 'object') return (v as { fa?: string }).fa
  return undefined
}

function SubBooleanButtons({
  subField, value, onChange, disabled,
}: {
  subField: SubFieldDefinition; value: unknown; onChange: (v: unknown) => void; disabled?: boolean
}) {
  const tc = subField.type_config ?? {}
  const yes = readLabelLoose(tc.true_label)  || 'بله'
  const no  = readLabelLoose(tc.false_label) || 'خیر'
  const set = (next: boolean) => onChange(value === next ? null : next)
  return (
    <div className="flex gap-1.5">
      {[{ v: true, label: yes }, { v: false, label: no }].map(({ v, label }) => {
        const selected = value === v
        return (
          <button
            key={String(v)}
            type="button"
            onClick={() => !disabled && set(v)}
            disabled={disabled}
            className={cn(
              'flex-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors',
              selected
                ? v
                  ? 'border-emerald-500 bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500'
                  : 'border-red-500 bg-red-500/10 text-red-300 ring-1 ring-red-500'
                : 'border-border bg-background text-foreground hover:border-foreground/40',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

function SubSingleSelect({
  subField, value, onChange, disabled,
}: {
  subField: SubFieldDefinition; value: unknown; onChange: (v: unknown) => void; disabled?: boolean
}) {
  const opts = (subField.type_config?.options as SelectOption[] | undefined) ?? []
  if (opts.length === 0) {
    return <div className="text-[11px] text-muted-foreground">گزینه‌ای تعریف نشده</div>
  }
  return (
    <div className="flex flex-wrap gap-1">
      {opts.map((o) => {
        const sel = value === o.option_key
        return (
          <button
            key={o.option_key}
            type="button"
            onClick={() => !disabled && onChange(sel ? null : o.option_key)}
            disabled={disabled}
            className={cn(
              'rounded-md border px-2 py-1 text-xs transition-colors',
              sel ? 'border-primary bg-primary/15 text-primary ring-1 ring-primary'
                  : 'border-border bg-background hover:border-foreground/40',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {o.label?.fa || o.option_key}
          </button>
        )
      })}
    </div>
  )
}

function SubMultiSelect({
  subField, value, onChange, disabled,
}: {
  subField: SubFieldDefinition; value: unknown; onChange: (v: unknown) => void; disabled?: boolean
}) {
  const opts = (subField.type_config?.options as SelectOption[] | undefined) ?? []
  const selected = (value as string[]) ?? []
  if (opts.length === 0) {
    return <div className="text-[11px] text-muted-foreground">گزینه‌ای تعریف نشده</div>
  }
  const toggle = (k: string) => {
    if (disabled) return
    onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k])
  }
  return (
    <div className="flex flex-wrap gap-1">
      {opts.map((o) => {
        const sel = selected.includes(o.option_key)
        return (
          <button
            key={o.option_key}
            type="button"
            onClick={() => toggle(o.option_key)}
            disabled={disabled}
            className={cn(
              'rounded-md border px-2 py-1 text-xs transition-colors',
              sel ? 'border-primary bg-primary/15 text-primary ring-1 ring-primary'
                  : 'border-border bg-background hover:border-foreground/40',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {o.label?.fa || o.option_key}
          </button>
        )
      })}
    </div>
  )
}
